import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { map } from 'rxjs';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ContentBuilderColorPipe } from '../../../core/pipes/content-builder-color.pipe';
import { AuthVerificationEmailService } from '@core/services/auth/auth-verification-email.service';

export type VerifiedUserEmailSuccessAlertModalData = ModalConfig<{
  handle: typeof VerifiedUserEmailSuccessAlertModalComponent.handle;
  inputs: {
    headingColor?: IContentBuilderFieldColor;
    closeIconFieldColors?: ContentBuilderFieldColors;
  };
}>;

@Component({
  selector: 'app-disable-prepayment-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalFillerAtomComponent,
    ButtonAtomComponent,
    TranslateModule,
    ContentBuilderColorPipe,
  ],
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="index()"
    ></app-modal-filler-atom>
    <div
      class="fixed left-0 right-0 overflow-y-auto shadow md:left-0 md:top-0 md:flex md:h-full md:w-full md:items-center md:justify-center mdMax:bottom-0 mdMax:mx-auto mdMax:max-h-85-screen mdMax:max-w-screen-sm"
      [style.z-index]="index() + 50"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="relative flex flex-col rounded-t-3xl bg-white p-5 md:max-w-lg md:rounded-3xl md:p-7"
        [style.z-index]="index() + 50"
      >
        <div class="space-y-5 px-4.5 py-5">
          <div class="flex items-center">
            <div
              class="flex flex-1 items-center"
              [style.color]="headingColor() | contentBuilderColor: '#000'"
            >
              <i class="icon icon-email mr-3"></i>
              <span class="font-base font-bold lg:text-md">{{
                'verified-user-email-success-modal.title' | translate
              }}</span>
            </div>
            <button
              (click)="close()"
              class="icon icon-cross h-8 w-8 rounded-full text-xs"
              [style.backgroundColor]="
                closeIconFieldColors()?.background
                  | contentBuilderColor: 'var(--ui-color-primary-200)'
              "
              [style.color]="
                closeIconFieldColors()?.font | contentBuilderColor: 'var(--ui-color-primary-900)'
              "
            ></button>
          </div>

          <div class="flex items-center justify-between">
            <p>
              {{ 'verified-user-email-success-modal.description' | translate }}
            </p>
          </div>

          <div class="flex items-center justify-center gap-5">
            <app-button-atom
              class="block flex-1"
              [label]="'verified-user-email-success-modal.done' | translate"
              [type]="'primary'"
              [color]="'primary'"
              [full]="true"
              (clickEventEmitter)="close()"
            />
          </div>
        </div>
      </div>
    </div>
  `,
})
export class VerifiedUserEmailSuccessAlertModalComponent
  extends ModalScrollBlockBase
  implements GetModalInterface<VerifiedUserEmailSuccessAlertModalData>
{
  static handle = 'verified-user-email-success-modal' as const;

  private readonly modalsService: ModalsService<VerifiedUserEmailSuccessAlertModalData> =
    inject(ModalsService);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly authVerificationEmailService = inject(AuthVerificationEmailService);

  public readonly headingColor = input<IContentBuilderFieldColor>();
  public readonly closeIconFieldColors = input<ContentBuilderFieldColors>();

  constructor() {
    super();
    this.authVerificationEmailService.clearLastSentTime();
  }

  protected readonly index = toSignal(
    this.modalsService
      .getData('verified-user-email-success-modal')
      .pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);

  protected close(): void {
    this.modalsService.close('verified-user-email-success-modal');
  }
}
